import type { AuditLogEntry } from '../../types/index';
import { LISTING_STATES } from '../../types/index';
import { actionLabel, fieldLabel } from './auditLabels';

function displayValue(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '(leer)';
  return `"${v}"`;
}

function stateLabel(key: string | null | undefined): string {
  if (!key) return '(leer)';
  return LISTING_STATES[key]?.label ?? key;
}

function formatHeadline(entry: AuditLogEntry): string {
  switch (entry.action) {
    case 'created':
      return entry.comment || 'Erstellt';
    case 'state_changed':
      return `Status: ${stateLabel(entry.old_value)} → ${stateLabel(entry.new_value)}`;
    case 'field_updated':
      return `${fieldLabel(entry.field_name)}: geändert`;
    case 'scrape_updated':
      return `${fieldLabel(entry.field_name)}: automatisch aktualisiert`;
    case 'annotation_added':
    case 'annotation_updated':
    case 'annotation_deleted':
      return `${actionLabel(entry.action)}${entry.field_name ? ` (${entry.field_name})` : ''}`;
    case 'assigned':
      return `Zuständigkeit: ${displayValue(entry.old_value)} → ${displayValue(entry.new_value)}`;
    default:
      return actionLabel(entry.action);
  }
}

interface SubBlockProps {
  diff: string | null;
  comment: string | null;
}

function shouldRenderDiff(entry: AuditLogEntry): boolean {
  if (entry.field_name === 'latitude' || entry.field_name === 'longitude') return false;
  // The headline for these actions already contains the diff; avoid showing it twice.
  if (entry.action === 'state_changed') return false;
  if (entry.action === 'assigned') return false;
  return entry.old_value !== null && entry.old_value !== undefined
    || entry.new_value !== null && entry.new_value !== undefined;
}

function SubBlock({ diff, comment }: SubBlockProps) {
  if (!diff && !comment) return null;
  return (
    <div className="mt-1 text-sm text-gray-600 italic bg-gray-50 rounded px-2 py-1 space-y-0.5">
      {diff && <div>{diff}</div>}
      {comment && <div className={diff ? 'text-xs text-gray-500' : ''}>{comment}</div>}
    </div>
  );
}

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-green-500',
  state_changed: 'bg-blue-500',
  field_updated: 'bg-yellow-500',
  scrape_updated: 'bg-gray-400',
  detail_scraped: 'bg-slate-500',
  period_override_added: 'bg-purple-500',
  period_override_removed: 'bg-purple-300',
  period_override_reset: 'bg-purple-200',
  document_uploaded: 'bg-indigo-500',
  document_generated: 'bg-indigo-400',
  document_updated: 'bg-indigo-300',
  document_deleted: 'bg-red-500',
  annotation_added: 'bg-amber-500',
  annotation_updated: 'bg-amber-400',
  annotation_deleted: 'bg-amber-300',
  assigned: 'bg-teal-500',
  bplan_assigned: 'bg-emerald-500',
  bplan_unassigned: 'bg-emerald-300',
  merged: 'bg-fuchsia-500',
  restored: 'bg-lime-500',
  soft_deleted: 'bg-slate-400',
  letter_sent: 'bg-blue-600',
  letter_draft_created: 'bg-blue-400',
  letter_draft_updated: 'bg-blue-300',
  letter_finalized: 'bg-blue-700',
  communication_created: 'bg-blue-400',
  communication_updated: 'bg-blue-300',
  communication_logged: 'bg-blue-400',
  communication_deleted: 'bg-red-400',
  letterhead_created: 'bg-cyan-500',
  letterhead_updated: 'bg-cyan-400',
  letterhead_deleted: 'bg-red-400',
};

const ACTION_ICONS: Record<string, string> = {
  created: '+',
  state_changed: 'S',
  field_updated: '✎',
  detail_scraped: '↻',
  document_uploaded: '↑',
  document_generated: '⚙',
  document_deleted: '×',
  annotation_added: '💬',
  letter_sent: '✉',
  letter_draft_created: '✎',
  letter_draft_updated: '✎',
  letter_finalized: '✓',
  communication_created: '💬',
  communication_updated: '✎',
  communication_logged: '💬',
  communication_deleted: '×',
  letterhead_created: '✦',
  letterhead_updated: '✎',
  letterhead_deleted: '×',
  assigned: '→',
  bplan_assigned: '🗺',
  bplan_unassigned: '×',
  restored: '↺',
  soft_deleted: '×',
};

export default function AuditTimeline({ entries }: { entries: AuditLogEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-gray-500 py-4">Noch keine Änderungen dokumentiert.</p>;
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {entries.map((entry, idx) => {
          const showDiff = shouldRenderDiff(entry);
          const diffText = showDiff
            ? `${displayValue(entry.old_value)} → ${displayValue(entry.new_value)}`
            : null;
          // Suppress comment when it duplicates the headline (e.g. "created" uses comment as headline).
          const commentText = entry.action === 'created' && entry.comment === formatHeadline(entry)
            ? null
            : entry.comment;

          return (
            <li key={entry.id}>
              <div className="relative pb-8">
                {idx < entries.length - 1 && (
                  <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                )}
                <div className="relative flex items-start space-x-3">
                  <div>
                    <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${ACTION_COLORS[entry.action] || 'bg-gray-400'}`}>
                      <span className="text-white text-xs">
                        {ACTION_ICONS[entry.action] || 'E'}
                      </span>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{entry.user_name ?? 'System'}</span>
                      <span className="text-gray-500 ml-2">
                        {new Date(entry.created_at).toLocaleString('de-DE')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-700">{formatHeadline(entry)}</p>
                    <SubBlock diff={diffText} comment={commentText} />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
